import { Injectable, Injector } from '@angular/core';
import { CapacitorHttp, HttpOptions, HttpResponse } from '@capacitor/core';
import { catchError, defer, map, Observable, tap, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { IAPICallOptions, IAPIResponse } from '../../../core/models/api.interface';
import { helpers } from '../../../shared/util/helpers';

@Injectable({
  providedIn: 'root'
})
export class APIService {
  baseUrl: string;
  private http!: HttpClient;

  constructor(injector: Injector) {
    this.http = injector.get(HttpClient);
    this.baseUrl = '/api';
  }

  get$<T = IAPIResponse>(path: string, queryParams?: object, callOptions?: IAPICallOptions): Observable<T> {
    let url = this.prependBase(path);

    return this.http
      .get(url, { ...(callOptions?.requestOptions ?? {}), params: helpers.toHttpParam(queryParams) })
      .pipe(
        map((response) => response as T),
        catchError((err) => {
          this.handleErrorResponse(err);
          return throwError(() => err);
        })
      );
  }

  post$<T = IAPIResponse>(path: string, body: object, queryParams?: object): Observable<T> {
    return this.http.post(this.prependBase(path), body, { params: helpers.toHttpParam(queryParams) }).pipe(
      tap((response) => {
        let res = response as IAPIResponse;
        if (res && res.response && res.response.token) {
          document.cookie = `CfafAuthToken=${res.response.token} ; path=/`;
          document.cookie = `CfafAdminAuthToken=${res.response.token} ;  path=/`;
        }
      }),
      map((response) => response as T),
      catchError((err) => {
        this.handleErrorResponse(err);
        return throwError(() => err);
      })
    );
  }

  put$<T = IAPIResponse>(
    path: string,
    body: object,
    queryParams?: string | object,
    callOptions?: IAPICallOptions
  ): Observable<T> {
    let url = this.prependBase(path);
    return this.http.put(url, body, { ...callOptions?.requestOptions, params: helpers.toHttpParam(queryParams) }).pipe(
      map((obj) => obj as T),
      catchError((err) => {
        this.handleErrorResponse(err);
        return throwError(() => err);
      })
    );
  }

  delete$<T = IAPIResponse>(path: string, body?: object): Observable<T> {
    const options = body
      ? {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        }),
        body: body
      }
      : {};

    return this.http.delete(this.prependBase(path), options).pipe(
      map((response) => response as T),
      catchError((error) => {
        this.handleErrorResponse(error);
        return throwError(() => error);
      })
    );
  }

  private handleErrorResponse(error: any) {
    let toastMessage: string = '';

    if (error.status !== null) {
      switch (error.status) {
        case 0:
          toastMessage = 'Network error';
          break;
        case 400:
          toastMessage = error?.error?.status?.statusDescription;
          break;
        case 401:
          toastMessage = 'Invalid token';
          break;
        case 403:
          toastMessage =
            'Your role does not have permission to access this tab or function. If you think this is a mistake, please contact your System Administrator.';
          break;
      }
      toastMessage ??= error.sText;
    }

    // this.toastSvc.error(toastMessage);
    console.error(toastMessage);
  }

  private prependBase(path: string): string {
    return `${this.baseUrl}${path}`;
  }
}
